import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import ProfileTabs from "./ProfileTabs";
import ProfileTabContent from "./ProfileTabContent";
import LoadingScreen from "../../components/LoadingScreen";
import { useProfile } from "../../hooks/useProfile";
import { useTheme } from "../../hooks/useTheme";
import EditBusiness from "./user-profile/EditBusiness";
import EditUserProfile from "./user-profile/EditUserProfile";
import { Theme } from "./types/profile";
import { colorOptions, fontOptions } from "./theme/themeConstants";
import { PageTitle } from "src/shared/PageTitle";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle, COMPANY_NAME } from "src/shared/utils/brand";

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState("Business Profile");
  const [editBusiness, setEditBusiness] = useState(false);
  const [editUser, setEditUser] = useState(false);
  // const [businessType, setBusinessType] = useState<string>("");
  const { profileData, loading, refetch } = useProfile();
  const { theme, loading: themeLoading, updateTheme } = useTheme();
  const { user } = useUser();

  useEffect(() => {
    document.title = pageTitle("Profile");
  }, []);
  const businessType = user?.businessType ?? "";

  const handleEditBusiness = () => setEditBusiness(true);
  const handleCloseEditBusiness = () => setEditBusiness(false);

  const handleEditUser = () => setEditUser(true);
  const handleCloseEditUser = () => setEditUser(false);

  const handleUpdateTheme = async (newTheme: Theme) => {
    return await updateTheme(newTheme);
  };

  const handleResetTheme = async () => {
    const defaultTheme: Theme = {
      backgroundColor: colorOptions["DodgerBlue"],
      fontColor: "white",
      fontFamily: fontOptions["Sans-serif"],
      businessName: sessionStorage.getItem("business_type") || COMPANY_NAME,
    };
    return await updateTheme(defaultTheme);
  };

  if (loading || themeLoading) {
    return <LoadingScreen />;
  }

  const fontClass = theme?.fontFamily ? fontOptions[theme.fontFamily] : "sans-serif";

  return (
    <div className={`bg-gray-100 w-full h-full flex flex-col ${fontClass}`}>
      <PageTitle title="- Profile" />
      <ToastContainer />
      <div className="flex-1 min-h-0 p-2 flex flex-col">
        <div className="mb-2 pb-2 border-b border-gray-200 shrink-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{activeTab}</h1>
        </div>

        {/* Stack Tab Navigation Bar above Main Content */}
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Horizontal Tabs Bar */}
          <div className="w-full max-w-2xl shrink-0">
            <ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} theme={theme} />
          </div>

          {/* Content Area */}
          <div className="thin-scrollbar bg-white rounded-xl shadow-xs border border-gray-200 p-3 flex-1 min-h-0 overflow-y-auto">
            <ProfileTabContent
              activeTab={activeTab}
              profileData={profileData}
              loading={loading}
              theme={theme}
              businessType={businessType}
              onEditBusiness={handleEditBusiness}
              onEditUser={handleEditUser}
              onUpdateTheme={handleUpdateTheme}
              onResetTheme={handleResetTheme}
            />
          </div>
        </div>
      </div>

      {/* Edit Modals */}
      {editBusiness && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
            <EditBusiness
              profileData={profileData}
              onClose={handleCloseEditBusiness}
              onSave={() => {
                refetch();
                handleCloseEditBusiness();
              }}
            />
          </div>
        </div>
      )}
      {editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
            <EditUserProfile
              profileData={profileData}
              onClose={handleCloseEditUser}
              onSave={() => {
                refetch();
                handleCloseEditUser();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
